import type { Spec } from "@json-render/core"
import { type Ux, type UxNode, type UxPage, orderedChildren, text } from "./model"
import { message } from "./messages"

/** Catalog element types. Every WidgetIR component kind maps to exactly one; nothing else is emitted. */
export type ElementType = "Section" | "TabSet" | "List" | "Detail" | "Form" | "ChildGrid" | "Field" | "Action" | "Navigation" | "Unsupported"

/**
 * Project an admitted WidgetIR page onto a json-render element map. WidgetIR stays canonical: the
 * projection carries node ids only, and components resolve data, conditions and actions through
 * Norn. json-render's own state, visibility and expression features are never used.
 */
export function projectPage(page: UxPage, ux: Ux, locale?: string): Spec {
  const elements: Spec["elements"] = {}
  const forms = new Set(ux.forms.map((f) => f.formId))

  const emit = (node: UxNode): string => {
    const c = node.component
    const id = node.nodeId
    switch (c.kind) {
      case "section":
        elements[id] = { type: "Section", props: { nodeId: id }, children: groupTabs(orderedChildren(node)) }
        return id
      case "detail":
        elements[id] = { type: "Detail", props: { nodeId: id }, children: groupTabs(orderedChildren(node)) }
        return id
      case "list":
        // Row actions and navigation render once per row inside the list, not as tree children.
        elements[id] = { type: "List", props: { nodeId: id, rowNodeIds: node.children.map((ch) => ch.nodeId) }, children: [] }
        return id
      case "form":
        elements[id] = forms.has(c.formId) ? { type: "Form", props: { nodeId: id }, children: node.children.map(emit) } : unsupported(message(locale ?? "en", "page.undeclaredForm", { formId: c.formId }))
        return id
      case "child_grid":
        elements[id] = { type: "ChildGrid", props: { nodeId: id }, children: [] }
        return id
      case "field":
        elements[id] = { type: "Field", props: { nodeId: id }, children: [] }
        return id
      case "action":
        elements[id] = { type: "Action", props: { nodeId: id }, children: [] }
        return id
      case "navigation":
        elements[id] = { type: "Navigation", props: { nodeId: id }, children: [] }
        return id
      case "composition":
        elements[id] = unsupported(message(locale ?? "en", "page.compositionUnavailable", { pageId: c.pageId, widgetId: c.widget.id }))
        return id
      default:
        // A component kind newer than this shell renders as explicitly unsupported, never silently.
        elements[id] = unsupported(message(locale ?? "en", "page.componentUnavailable", { kind: (c as { kind: string }).kind }))
        return id
    }
  }

  // Consecutive tab sections form one tab set.
  const groupTabs = (children: readonly UxNode[]): string[] => {
    const out: string[] = []
    let run: UxNode[] = []
    const flush = () => {
      if (run.length === 0) return
      const tabsId = `tabs:${run[0]?.nodeId ?? ""}`
      elements[tabsId] = {
        type: "TabSet",
        props: { tabs: run.map((t) => ({ nodeId: t.nodeId, label: t.component.kind === "section" ? text(t.component.section.label, locale) : t.nodeId })) },
        children: run.map(emit),
      }
      out.push(tabsId)
      run = []
    }
    for (const child of children) {
      if (child.component.kind === "section" && child.component.section.kind === "tab") run.push(child)
      else {
        flush()
        out.push(emit(child))
      }
    }
    flush()
    return out
  }

  const unsupported = (reason: string) => ({ type: "Unsupported" as const, props: { reason }, children: [] })
  const root = emit(page.root)
  return { root, elements }
}

/** Every node of a page by id, so components resolve their WidgetIR node from the projected id. */
export function nodeIndex(page: UxPage): Map<string, UxNode> {
  const out = new Map<string, UxNode>()
  const visit = (n: UxNode) => {
    out.set(n.nodeId, n)
    n.children.forEach(visit)
  }
  visit(page.root)
  return out
}
